import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import Cookies from 'universal-cookie';
import axios from 'axios';
import SignInPopup from './SignInPopup';
import RegionPromptModal from './RegionPromptModal';
import './navbar.css';

const REGION_LABELS = {
    centurion: 'Centurion',
    eastern_cape: 'Eastern Cape',
    freestate: 'Free State',
    johannesburg: 'Johannesburg',
    kwa_zulu_natal: 'KwaZulu-Natal',
    limpopo: 'Limpopo',
    mpumalanga: 'Mpumalanga',
    north_cape: 'Northern Cape',
    north_west: 'North West',
    pretoria: 'Pretoria',
    southern_cape: 'Southern Cape',
    western_cape: 'Western Cape'
};

export const Navbar = () => {
    const cookies = new Cookies();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [username, setUsername] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isSearchDropdownOpen, setIsSearchDropdownOpen] = useState(false);
    const [isPowerUser, setIsPowerUser] = useState(false);
    const [preferredRegion, setPreferredRegion] = useState(localStorage.getItem('preferredRegion') || '');
    const [showRegionModal, setShowRegionModal] = useState(false);
    const [showSignInPopup, setShowSignInPopup] = useState(false);
    const menuRef = useRef(null);

    // Check authentication status with token expiration validation
    const checkAuthStatus = () => {
        const cookieToken = cookies.get('access_token');
        const localStorageToken = localStorage.getItem('auth_token');
        const token = cookieToken || localStorageToken;
        
        if (!token) {
            setIsAuthenticated(false);
            setUsername('');
            setIsPowerUser(false);
            return;
        }
        
        // Validate token expiration
        try {
            const parts = token.split('.');
            if (parts.length !== 3) {
                // Invalid token format
                setIsAuthenticated(false);
                setUsername('');
                return;
            }
            
            const payload = JSON.parse(atob(parts[1]));
            const now = Math.floor(Date.now() / 1000);
            
            // Check if token is expired
            if (payload.exp && payload.exp < now) {
                // Token expired - clear it
                cookies.remove('access_token');
                localStorage.removeItem('auth_token');
                localStorage.removeItem('userID');
                localStorage.removeItem('username');
                localStorage.removeItem('isPowerUser');
                setIsAuthenticated(false);
                setUsername('');
                setIsPowerUser(false);
                return;
            }
            
            // Token is valid
            setIsAuthenticated(true);
            const storedUsername = window.localStorage.getItem('username');
            if (storedUsername) {
                setUsername(storedUsername);
            }
            // Check if user is a power user
            const storedIsPowerUser = window.localStorage.getItem('isPowerUser');
            setIsPowerUser(storedIsPowerUser === 'true');
        } catch (error) {
            console.error('Error validating token in navbar:', error);
            setIsAuthenticated(false);
            setUsername('');
            setIsPowerUser(false);
        }
    };

    useEffect(() => {
        // Initial auth check
        checkAuthStatus();

        // Listen for authentication events
        const handleAuthSuccess = () => {
            checkAuthStatus();
        };

        const handleAuthTokenUpdated = () => {
            checkAuthStatus();
            setPreferredRegion(localStorage.getItem('preferredRegion') || '');
        };

        const handleLogout = () => {
            // Immediately clear state on logout event
            setIsAuthenticated(false);
            setUsername('');
            setIsPowerUser(false);
            setPreferredRegion('');
            // Also check auth status to ensure everything is cleared
            checkAuthStatus();
        };

        const handleRegionUpdated = (event) => {
            if (event?.detail?.preferredRegion !== undefined) {
                setPreferredRegion(event.detail.preferredRegion || '');
            } else {
                setPreferredRegion(localStorage.getItem('preferredRegion') || '');
            }
        };

        // Add event listeners
        window.addEventListener('auth-success', handleAuthSuccess);
        window.addEventListener('auth-token-updated', handleAuthTokenUpdated);
        window.addEventListener('auth-logout', handleLogout);
        window.addEventListener('region-updated', handleRegionUpdated);

        // Close menu when clicking outside
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('auth-success', handleAuthSuccess);
            window.removeEventListener('auth-token-updated', handleAuthTokenUpdated);
            window.removeEventListener('auth-logout', handleLogout);
            window.removeEventListener('region-updated', handleRegionUpdated);
        };
    }, []);

    const logout = () => {
        // Immediately update state to reflect logout
        setIsAuthenticated(false);
        setUsername('');
        setIsPowerUser(false);
        
        // Remove cookies with same options used when setting them (for production)
        const hostname = window.location.hostname;
        const isProduction = hostname !== 'localhost' && hostname !== '127.0.0.1';
        
        // Remove cookie with same path and domain options
        cookies.remove("access_token", {
            path: '/',
            sameSite: 'lax',
            secure: isProduction,
            ...(isProduction && hostname.includes('.') && { domain: hostname.startsWith('www.') ? hostname.substring(4) : hostname })
        });
        
        // Also try removing without domain (fallback)
        cookies.remove("access_token", { path: '/' });
        
        // Clear localStorage
        window.localStorage.removeItem("userID");
        window.localStorage.removeItem("username");
        window.localStorage.removeItem("auth_token");
        window.localStorage.removeItem("isPowerUser");
        window.localStorage.removeItem("preferredRegion");
        window.localStorage.removeItem("vitalityFirst");
        
        // Clear axios default headers
        if (axios.defaults && axios.defaults.headers && axios.defaults.headers.common) {
            delete axios.defaults.headers.common['Authorization'];
        }
        
        // Dispatch logout event to notify other components
        window.dispatchEvent(new CustomEvent('auth-logout', {
            detail: { 
                loggedOut: true
            }
        }));
        
        // Navigate to login page
        navigate("/auth/login", { replace: true });
    };

    const toggleMenu = () => {
        setIsOpen(!isOpen);
        // Close search dropdown when main menu closes
        if (isOpen) {
            setIsSearchDropdownOpen(false);
        }
    };

    const toggleSearchDropdown = (e) => {
        e.preventDefault();
        setIsSearchDropdownOpen(!isSearchDropdownOpen);
    };

    const canCreateRecipes = isPowerUser;
    const isHiggo = username === 'Higgo' && isPowerUser;
    const regionLabel = REGION_LABELS[preferredRegion] || 'Select Region';

    const handleRegionButtonClick = () => {
        if (isAuthenticated) {
            setShowRegionModal(true);
            return;
        }
        setShowSignInPopup(true);
    };

    return (
        <div className="navbar-container">
            <div className="max-w-7xl mx-auto px-4 h-full flex justify-between items-center">
                <Link to="/" className="group">
                    <div className="navbar-logo">
                        <div className="navbar-logo-maak">
                            WHAT
                        </div>
                        <div className="navbar-logo-kos">
                            TO MAKE
                        </div>
                    </div>
                </Link>

                {/* Desktop Navigation */}
                <div className="hidden md:flex items-center space-x-8">
                    {/* <Link to="/searchrecipes" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                        All recipes
                    </Link> */}
                    {/* <Link to="/searchrecipes?category=family meal" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                        Weeknight dinner
                    </Link>
                    <Link to="/searchrecipes?category=quick and easy" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                        Easy lunches
                    </Link>

                    <Link to="/searchrecipes?category=meal prep" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                        Meal prep
                    </Link>
                    <Link to="/searchrecipes?dietary=vegetarian" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                        Vegetarian
                    </Link> */}
                    <Link to="/searchrecipes" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                        Search Recipes
                    </Link>
                    {/* <Link to="/meal-planning" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                        Meal Planning
                    </Link> */}
                    {isAuthenticated && (
                        <>
                            <Link to="/cupboard" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                                My Cupboard
                            </Link>
                            <Link to="/shopping-list" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                                Shopping List
                            </Link>
                        </>
                    )}
                    {/* <Link to="/premium" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                        Premium
                    </Link> */}
                    {isAuthenticated && canCreateRecipes && (
                        <Link to="/createrecipe" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                            Create Recipe
                        </Link>
                    )}
                </div>

                {/* Desktop Auth Buttons */}
                <div className="hidden md:flex items-center space-x-4">
                    <button
                        type="button"
                        onClick={handleRegionButtonClick}
                        className="navbar-region-button"
                    >
                        Region: {regionLabel}
                    </button>
                    {!isAuthenticated ? (
                        <>
                            <Link to="/auth/login" className="text-gray-700 hover:text-gray-900 font-medium transition-colors">
                                Log In
                            </Link>
                            <Link to="/auth/register" className="bg-green-600 text-white px-6 py-2 rounded-full font-medium hover:bg-green-700 transition-colors">
                                Join WTM
                            </Link>
                        </>
                    ) : (
                        <div className="flex items-center space-x-4">
                            <span className="text-gray-700 font-medium">Welcome, {username}</span>
                            <button 
                                onClick={logout}
                                className="text-gray-700 hover:text-gray-900 font-medium transition-colors"
                            >
                                Logout
                            </button>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-2 md:gap-3">
                    <button
                        type="button"
                        onClick={handleRegionButtonClick}
                        className="navbar-region-button navbar-region-button-mobile md:hidden"
                    >
                        {regionLabel}
                    </button>
                    <div className="relative" ref={menuRef}>
                    <button 
                        className="navbar-menu-button"
                        onClick={toggleMenu}
                        aria-expanded={isOpen}
                        aria-label="Menu"
                    >
                        <div className="flex flex-col justify-between w-6 h-5">
                            <span className={`hamburger-line hamburger-line-top ${isOpen ? 'rotate-45 translate-y-2' : ''}`}></span>
                            <span className={`hamburger-line hamburger-line-middle ${isOpen ? 'opacity-0' : 'opacity-100'}`}></span>
                            <span className={`hamburger-line hamburger-line-bottom ${isOpen ? '-rotate-45 -translate-y-2' : ''}`}></span>
                        </div>
                    </button>
                    
                    {isOpen && (
                        <div className="navbar-dropdown">
                            <div className="navbar-dropdown-gradient"></div>
                            
                            <Link to="/" onClick={toggleMenu} className="navbar-menu-item">
                                <span className="flex items-center">
                                    <svg className="w-5 h-5 mr-2 navbar-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m0 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                                    </svg>
                                    Home
                                </span>
                            </Link>
                            <div className="navbar-menu-section">
                                <div className="navbar-menu-item navbar-menu-item-clickable" onClick={toggleSearchDropdown}>
                                    <span className="flex items-center justify-between">
                                        <span className="flex items-center">
                                            <svg className="w-5 h-5 mr-2 navbar-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                                            </svg>
                                            Search Recipes
                                        </span>
                                        <svg 
                                            className={`w-4 h-4 transition-transform duration-200 ${isSearchDropdownOpen ? 'rotate-180' : ''}`} 
                                            fill="none" 
                                            stroke="currentColor" 
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </span>
                                </div>
                                
                                {/* Recipe Categories Dropdown */}
                                <div className={`navbar-submenu ${isSearchDropdownOpen ? 'navbar-submenu-open' : 'navbar-submenu-closed'}`}>
                                <Link to="/searchrecipes" onClick={toggleMenu} className="navbar-submenu-item">
                                        <span className="flex items-center">
                                            <span className="navbar-bullet-point"></span>
                                            Latest
                                        </span>
                                    </Link>
                                    <Link to="/searchrecipes?category=flavoured chicken" onClick={toggleMenu} className="navbar-submenu-item">
                                        <span className="flex items-center">
                                            <span className="navbar-bullet-point"></span>
                                            Flavoursome Chicken
                                        </span>
                                    </Link>
                                    <Link to="/searchrecipes?category=break a egg breakfasts" onClick={toggleMenu} className="navbar-submenu-item">
                                        <span className="flex items-center">
                                            <span className="navbar-bullet-point"></span>
                                            Break-a-Egg Breakfasts
                                        </span>
                                    </Link>
                                    <Link to="/searchrecipes?category=cape malay indian" onClick={toggleMenu} className="navbar-submenu-item">
                                        <span className="flex items-center">
                                            <span className="navbar-bullet-point"></span>
                                            Cape Malay & Indian Spice
                                        </span>
                                    </Link>
                                    <Link to="/searchrecipes?category=south african" onClick={toggleMenu} className="navbar-submenu-item">
                                        <span className="flex items-center">
                                            <span className="navbar-bullet-point"></span>
                                            Proudly South African
                                        </span>
                                    </Link>
                                    <Link to="/searchrecipes?category=baked goods" onClick={toggleMenu} className="navbar-submenu-item">
                                        <span className="flex items-center">
                                            <span className="navbar-bullet-point"></span>
                                            Oven-Fresh Baked Goods
                                        </span>
                                    </Link>
                                    <Link to="/searchrecipes?category=braai & side dishes" onClick={toggleMenu} className="navbar-submenu-item">
                                        <span className="flex items-center">
                                            <span className="navbar-bullet-point"></span>
                                            Braai & Sides
                                        </span>
                                    </Link>
                                    <Link to="/searchrecipes?category=vegetarian" onClick={toggleMenu} className="navbar-submenu-item">
                                        <span className="flex items-center">
                                            <span className="navbar-bullet-point"></span>
                                            Garden Fresh Veggie
                                        </span>
                                    </Link>
                                    <Link to="/searchrecipes?category=decadently healthy desserts" onClick={toggleMenu} className="navbar-submenu-item">
                                        <span className="flex items-center">
                                            <span className="navbar-bullet-point"></span>
                                            Decadently Healthy Desserts
                                        </span>
                                    </Link>
                                    <Link to="/searchrecipes?category=healthy classics" onClick={toggleMenu} className="navbar-submenu-item">
                                        <span className="flex items-center">
                                            <span className="navbar-bullet-point"></span>
                                            Healthy Classics
                                        </span>
                                    </Link>
                                    <Link to="/searchrecipes?dietary=vegan" onClick={toggleMenu} className="navbar-submenu-item">
                                        <span className="flex items-center">
                                            <span className="navbar-bullet-point"></span>
                                            Vegan
                                        </span>
                                    </Link>
                                </div>
                            </div>
                            
                            {isAuthenticated && (
                                <>
                                    {isHiggo && (
                                        <>
                                            <Link to="/ingredient-management" onClick={toggleMenu} className="navbar-menu-item">Manage Ingredients</Link>
                                            <Link to="/ingredient-categories" onClick={toggleMenu} className="navbar-menu-item">Ingredient Categories</Link>
                                            <Link to="/admin/power-users" onClick={toggleMenu} className="navbar-menu-item">Power User Management</Link>
                                            <Link to="/admin/discount-codes" onClick={toggleMenu} className="navbar-menu-item">Discount Codes</Link>
                                        </>
                                    )}

                                     {isPowerUser && (
                                        <Link to={`/profile/${window.localStorage.getItem('userID')}`} onClick={toggleMenu} className="navbar-menu-item">
                                            <span className="flex items-center">
                                                <svg className="w-5 h-5 mr-2 navbar-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                                                </svg>
                                                My Profile
                                            </span>
                                        </Link>
                                    )} 
{/*                                     
                                    <Link to="/feed" onClick={toggleMenu} className="navbar-menu-item">Following Feed</Link>
                                    <Link to="/search-users" onClick={toggleMenu} className="navbar-menu-item">Find Users</Link>
                                     */}
                                    {canCreateRecipes && (
                                        <Link to="/createrecipe" onClick={toggleMenu} className="navbar-menu-item">
                                            <span className="flex items-center">
                                                <svg className="w-5 h-5 mr-2 navbar-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                                </svg>
                                                Create Recipe
                                            </span>
                                        </Link>
                                    )}
                                    
                                    <Link to="/savedrecipes" onClick={toggleMenu} className="navbar-menu-item">
                                        <span className="flex items-center">
                                            <svg className="w-5 h-5 mr-2 navbar-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path>
                                            </svg>
                                            Saved Recipes
                                        </span>
                                    </Link>
                                    <Link to="/meal-planning" onClick={toggleMenu} className="navbar-menu-item">
                                        <span className="flex items-center">
                                            <svg className="w-5 h-5 mr-2 navbar-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                                            </svg>
                                            Meal Planning
                                        </span>
                                    </Link>
                                    <Link to="/meal-prep-draft" onClick={toggleMenu} className="navbar-menu-item">
                                        <span className="flex items-center">
                                            <svg className="w-5 h-5 mr-2 navbar-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                            </svg>
                                            Meal prep draft
                                        </span>
                                    </Link>
                                    <Link to="/shopping-list" onClick={toggleMenu} className="navbar-menu-item">
                                        <span className="flex items-center">
                                            <svg className="w-5 h-5 mr-2 navbar-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"></path>
                                            </svg>
                                            Shopping List
                                        </span>
                                    </Link>
                                    <Link to="/preferences" onClick={toggleMenu} className="navbar-menu-item">
                                        <span className="flex items-center">
                                            <svg className="w-5 h-5 mr-2 navbar-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
                                            </svg>
                                            Preferences
                                        </span>
                                    </Link>
                                    {/* <Link to="/premium" onClick={toggleMenu} className="navbar-menu-item">
                                        <span className="flex items-center">
                                            <svg className="w-5 h-5 mr-2 navbar-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"/>
                                            </svg>
                                            Premium
                                        </span>
                                    </Link> */}
                                    <Link to="/cupboard" onClick={toggleMenu} className="navbar-menu-item">
                                        <span className="flex items-center">
                                            <svg className="w-5 h-5 mr-2 navbar-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path>
                                            </svg>
                                            My Cupboard
                                        </span>
                                    </Link>
                                </>
                            )}
                            
                            {!isAuthenticated ? (
                                <>
                                    <Link to="/auth/login" onClick={toggleMenu} className="navbar-menu-item-primary">Login</Link>
                                    <Link to="/auth/register" onClick={toggleMenu} className="navbar-menu-item-primary">Register</Link>
                                </>
                            ) : (
                                <button 
                                    className="navbar-menu-item-danger"
                                    onClick={() => { logout(); toggleMenu(); }}
                                >
                                    <span className="flex items-center">
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                                        </svg>
                                        Logout
                                    </span>
                                </button>
                            )}
                        </div>
                    )}
                    </div>
                </div>
            </div>
            {showRegionModal && (
                <RegionPromptModal onClose={() => setShowRegionModal(false)} />
            )}
            <SignInPopup
                show={showSignInPopup}
                onClose={() => setShowSignInPopup(false)}
                subtitle="Please sign in to set your shopping region."
            />
        </div>
    );
};

export default Navbar;
